import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Shield, Sparkles, Star, ArrowRight, Crown, MapPin } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";

const HERO_IMG = "https://images.pexels.com/photos/8153967/pexels-photo-8153967.jpeg?auto=compress&cs=tinysrgb&h=650&w=940";
const SECONDARY_IMG = "https://images.pexels.com/photos/8087992/pexels-photo-8087992.jpeg?auto=compress&cs=tinysrgb&h=650&w=940";

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user ? "/discover" : "/register";
  const ctaLabel = user ? "Open the app" : "Create a free account";
  const [champion, setChampion] = useState(null);
  const [topRaters, setTopRaters] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);

  useEffect(() => {
    api.get("/champion").then((r) => setChampion(r.data.champion)).catch(() => {});
    api.get("/top-raters").then((r) => setTopRaters(r.data.top_raters || [])).catch(() => {});
    api.get("/top-referrers").then((r) => setTopReferrers(r.data.top_referrers || [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="grain-overlay bg-[#faf9f6] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-12 items-center relative z-10">
          <div className="md:col-span-7">
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]" data-testid="hero-tagline">
              Dating, redefined for 55+
            </span>
            <h1 className="font-serif-display mt-6 text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight text-[#1a1a1a]">
              Fall in love,<br />
              <span className="text-[#722f37] italic">in your prime.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-[#5c5c5c]">
              A tasteful, private community for singles over 55. Rate photos, discover matches
              you'll actually love, and only pay when you find someone worth meeting.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={ctaTo} className="btn-primary flex items-center gap-2" data-testid="hero-primary-cta">
                {ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#how" className="btn-outline" data-testid="hero-secondary-cta">
                How it works
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-[#5c5c5c]">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#722f37]" /> Moderated & private</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#c39d2b]" /> Free signup</div>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[2rem] bg-[#eae5d9]/70 -z-10" />
              <img
                src={HERO_IMG}
                alt="A senior couple embracing"
                className="rounded-[1.75rem] w-full h-[560px] object-cover card-elev"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 glass px-5 py-4 rounded-2xl w-64" data-testid="hero-stat-card">
                <div className="flex items-center gap-2 text-[#722f37]">
                  <Star className="w-4 h-4 text-[#c39d2b] fill-[#c39d2b]" />
                  <span className="font-medium">4.9 avg. profile rating</span>
                </div>
                <p className="text-xs text-[#5c5c5c] mt-1">
                  Kept classy by our members.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHAMPION WEEKLY */}
      {champion && (
        <section className="bg-[#722f37] text-white py-14 md:py-20 border-t border-[#8c3a45]" data-testid="champion-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-4 relative">
              <div className="relative rounded-[1.75rem] overflow-hidden card-elev" style={{ boxShadow: "0 24px 60px rgba(196,157,43,0.32)" }}>
                <img src={champion.photo} alt={champion.display_name} className="w-full aspect-[4/5] object-cover" data-testid="champion-photo" />
                <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold text-[#2a2010]" style={{ background: "linear-gradient(180deg,#e5c65b 0%,#c39d2b 100%)" }}>
                  <Crown className="w-3.5 h-3.5" /> This week's #1
                </div>
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="flex items-center gap-2 text-[#e5c65b]">
                <Crown className="w-5 h-5" />
                <span className="text-sm uppercase tracking-[0.28em] font-bold">Champion of the week</span>
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">
                Meet <span className="text-[#e5c65b]" data-testid="champion-name">{champion.display_name}, {champion.age}</span>.
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-white/85">
                {champion.location && (
                  <span className="inline-flex items-center gap-1 text-sm"><MapPin className="w-4 h-4" /> {champion.location}</span>
                )}
                {champion.verified && <VerifiedBadge size={12} />}
                <span className="inline-flex items-center gap-1 text-sm font-semibold">
                  <Star className="w-4 h-4 text-[#e5c65b] fill-[#e5c65b]" /> {champion.week_avg}/10 · {champion.week_ratings} ratings this week
                </span>
              </div>
              <p className="mt-6 text-lg text-white/85 max-w-xl">
                Every week our members rate photos 1–10. The highest-rated profile is featured here — and can be contacted the moment you join.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={user ? "/top-rated" : "/register"} className="btn-gold" data-testid="champion-cta">
                  {user ? "See the top 10" : "Join to say hello"}
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section id="how" className="bg-white py-20 md:py-28 border-t border-[#e5e0d8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">How it works</span>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight text-[#1a1a1a]">
              A gentler way to meet<br /> the right person.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10 mt-16">
            {[
              { n: "01", t: "Create your profile", d: "Free forever. Add a bio, a few tasteful photos, and what you're looking for." },
              { n: "02", t: "Rate & be rated", d: "Members rate photos 1–10. Photos averaging 8+ become Golden — every viewer sees them." },
              { n: "03", t: "Reveal a match", d: "One tap and $10 reveals a Golden member and opens a chat. Bundles and membership drop the price." },
            ].map((s) => (
              <div key={s.n} className="p-8 rounded-2xl bg-[#faf9f6] border border-[#e5e0d8] card-elev" data-testid={`how-step-${s.n}`}>
                <div className="text-[#c39d2b] font-serif-display text-4xl">{s.n}</div>
                <h3 className="font-serif-display text-2xl mt-2 text-[#722f37]">{s.t}</h3>
                <p className="mt-3 text-[#5c5c5c] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOP RATERS */}
      {topRaters.length > 0 && (
        <section className="bg-[#faf9f6] py-20 md:py-24 border-t border-[#e5e0d8]" data-testid="top-raters-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between flex-wrap gap-4 max-w-3xl">
              <div>
                <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Community champions</span>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">This month's most active raters.</h2>
                <p className="mt-3 text-lg text-[#5c5c5c]">The members who power the ratings — without them, no leaderboards.</p>
              </div>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5">
              {topRaters.map((r, i) => (
                <div key={i} className="p-5 rounded-2xl bg-white border border-[#e5e0d8] card-elev flex flex-col items-center text-center" data-testid={`top-rater-${i}`}>
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-[#eae5d9] mb-3">
                    {r.photo && <img src={r.photo} alt={r.display_name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="font-serif-display text-xl text-[#722f37]">{r.display_name}, {r.age}</div>
                  {r.verified && <div className="mt-1"><VerifiedBadge size={10} /></div>}
                  <div className="mt-3 font-semibold text-2xl text-[#c39d2b]" data-testid={`top-rater-count-${i}`}>{r.ratings_given}</div>
                  <div className="text-xs text-[#5c5c5c]">ratings this month</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VALUE STRIP */}
      <section className="bg-[#faf9f6] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img src={SECONDARY_IMG} alt="Mature couple enjoying wine" className="rounded-[1.75rem] w-full h-[520px] object-cover card-elev" />
          </div>
          <div>
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Built for grown-ups</span>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">
              No games. No noise.<br /> Just <span className="italic text-[#722f37]">good matches</span>.
            </h2>
            <ul className="mt-8 space-y-5 text-lg text-[#1a1a1a]">
              <li className="flex gap-3"><Heart className="w-6 h-6 text-[#722f37] shrink-0 mt-0.5" /> Every member is verified 55+.</li>
              <li className="flex gap-3"><Shield className="w-6 h-6 text-[#722f37] shrink-0 mt-0.5" /> Photos moderated. Clothed only. Kept classy.</li>
              <li className="flex gap-3"><Sparkles className="w-6 h-6 text-[#722f37] shrink-0 mt-0.5" /> You only pay when a match happens.</li>
            </ul>
            <div className="mt-10">
              <Link to={ctaTo} className="btn-primary" data-testid="value-cta">
                {ctaLabel}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section id="pricing" className="bg-white py-20 md:py-28 border-t border-[#e5e0d8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Simple pricing</span>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight">Pay once — or unlock everything.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {[
              { name: "One reveal", price: "$10", per: "per match", desc: "Perfect if you're only curious about one connection.", key: "match_reveal" },
              { name: "Monthly", price: "$19.99", per: "per month", desc: "Unlimited match reveals + priority visibility.", featured: true, key: "prime_monthly" },
              { name: "Yearly", price: "$144", per: "per year", desc: "Best value. Unlimited reveals for a full year.", key: "prime_yearly" },
            ].map((p) => (
              <div
                key={p.key}
                className={`p-8 rounded-2xl border card-elev ${p.featured ? "bg-[#722f37] text-white border-[#722f37]" : "bg-[#faf9f6] border-[#e5e0d8]"}`}
                data-testid={`plan-${p.key}`}
              >
                <div className={`text-sm uppercase tracking-[0.22em] font-bold ${p.featured ? "text-[#e5c65b]" : "text-[#722f37]"}`}>{p.name}</div>
                <div className="flex items-baseline gap-2 mt-4">
                  <div className="font-serif-display text-5xl">{p.price}</div>
                  <div className={`text-sm ${p.featured ? "text-white/70" : "text-[#5c5c5c]"}`}>{p.per}</div>
                </div>
                <p className={`mt-4 ${p.featured ? "text-white/85" : "text-[#5c5c5c]"}`}>{p.desc}</p>
                <div className="mt-8">
                  <Link
                    to={user ? "/pricing" : "/register"}
                    className={p.featured ? "btn-gold" : "btn-outline"}
                    data-testid={`plan-cta-${p.key}`}
                  >
                    {user ? "Choose" : "Join free to buy"}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOP REFERRERS */}
      {topReferrers.length > 0 && (
        <section className="bg-white py-16 md:py-20 border-t border-[#e5e0d8]" data-testid="top-referrers-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="max-w-2xl">
              <span className="text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">Growth champions</span>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">Members who bring their friends.</h2>
              <p className="mt-3 text-[#5c5c5c]">Every referral earns them a free reveal. Share your code from your profile to join them.</p>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {topReferrers.map((r, i) => (
                <div key={i} className="p-4 rounded-2xl bg-[#faf9f6] border border-[#e5e0d8] card-elev flex flex-col items-center text-center" data-testid={`top-referrer-${i}`}>
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-[#eae5d9] mb-2">
                    {r.photo && <img src={r.photo} alt={r.display_name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="font-serif-display text-lg text-[#722f37]">{r.display_name}, {r.age}</div>
                  {r.verified && <div className="mt-1"><VerifiedBadge size={10} /></div>}
                  <div className="mt-2 font-semibold text-xl text-[#c39d2b]" data-testid={`top-referrer-count-${i}`}>{r.count}</div>
                  <div className="text-xs text-[#5c5c5c]">friend{r.count === 1 ? "" : "s"} brought</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="bg-[#faf9f6] py-10 border-t border-[#e5e0d8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-4 text-sm text-[#5c5c5c]">
          <div>© {new Date().getFullYear()} Prime Connect · Tasteful mature dating, moderated.</div>
          <div className="flex gap-6"><a href="#" className="hover:text-[#722f37]">Privacy</a><a href="#" className="hover:text-[#722f37]">Terms</a></div>
        </div>
      </footer>
    </div>
  );
}
